import type { Page } from '../types';
import type { PlotOptions, PositionedItem } from '../types/item';

type LegacyGaugePlot = Omit<PlotOptions, 'chartType'> & {
  chartType?: PlotOptions['chartType'] | 'gauge';
  gaugeValue?: string;
  gaugePrimaryText?: string;
  gaugePrimaryLabel?: string;
  gaugeSubText?: string;
  gaugeMinLabel?: string;
  gaugeMaxLabel?: string;
};

const LEGACY_GAUGE_FIELDS = [
  'gaugeValue',
  'gaugePrimaryText',
  'gaugePrimaryLabel',
  'gaugeSubText',
  'gaugeMinLabel',
  'gaugeMaxLabel',
] as const;

const isLegacyGaugePlot = (plot: LegacyGaugePlot): boolean =>
  plot.chartType === 'gauge' ||
  LEGACY_GAUGE_FIELDS.some((field) => plot[field] !== undefined);

const titleFromSubText = (
  title: PositionedItem['title'],
  subText: string | undefined
): PositionedItem['title'] =>
  !title?.trim() && subText?.trim() && !subText.includes('{{')
    ? subText.trim()
    : title;

const migrateLegacyGaugeItem = (item: PositionedItem): PositionedItem => {
  const legacyPlot = item.plot as LegacyGaugePlot | undefined;
  if (!legacyPlot || !isLegacyGaugePlot(legacyPlot)) return item;

  const {
    chartType,
    gaugeValue,
    gaugePrimaryText: _primaryText,
    gaugePrimaryLabel: _primaryLabel,
    gaugeSubText,
    gaugeMinLabel,
    gaugeMaxLabel,
    gaugeV2,
    ...plot
  } = legacyPlot;

  if (chartType !== 'gauge' && chartType !== 'gauge-v2') {
    return {
      ...item,
      plot: { ...plot, chartType, ...(gaugeV2 && { gaugeV2 }) },
    };
  }

  const value = gaugeV2?.value ?? gaugeValue;
  const minLabel = gaugeV2?.minLabel ?? gaugeMinLabel;
  const maxLabel = gaugeV2?.maxLabel ?? gaugeMaxLabel;
  // Only an arc gauge showed its sub text; a gauge-v2 never did.
  const title =
    chartType === 'gauge'
      ? titleFromSubText(item.title, gaugeSubText)
      : item.title;

  return {
    ...item,
    ...(title !== item.title && { title, isTitleManuallySet: true }),
    plot: {
      ...plot,
      chartType: 'gauge-v2',
      gaugeV2: {
        ...gaugeV2,
        ...(value !== undefined && { value }),
        ...(minLabel !== undefined && { minLabel }),
        ...(maxLabel !== undefined && { maxLabel }),
      },
    },
  };
};

export const migrateLegacyGauges = (page: Page): Page => ({
  ...page,
  tabs: page.tabs.map((tab) => ({
    ...tab,
    tabContent: {
      ...tab.tabContent,
      items: tab.tabContent?.items?.map(migrateLegacyGaugeItem) ?? [],
    },
  })),
});
